import React, { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faBars, faTimes } from "@fortawesome/free-solid-svg-icons";

// Utility functions outside component
const classNames = (...classes) => classes.filter(Boolean).join(' ');

function throttle(func, limit) {
  let lastFunc;
  let lastRan;
  return function() {
    const context = this;
    const args = arguments;
    if (!lastRan) {
      func.apply(context, args);
      lastRan = Date.now();
    } else {
      clearTimeout(lastFunc);
      lastFunc = setTimeout(function() {
        if ((Date.now() - lastRan) >= limit) {
          func.apply(context, args);
          lastRan = Date.now();
        }
      }, limit - (Date.now() - lastRan));
    }
  };
}

const Header = ({ toggleTheme, isDarkMode }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  const navItems = useMemo(() => ['home', 'about', 'services', 'contact'], []);

  useEffect(() => {
    document.documentElement.style.scrollBehavior = "smooth";

    const handleScroll = () => {
      setIsScrolled(window.scrollY > window.innerHeight * 0.1);
    };

    const throttledScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledScroll);
    
    return () => window.removeEventListener('scroll', throttledScroll);
  }, []);

  useEffect(() => {
    const sections = document.querySelectorAll("section");
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          setActiveSection(entry.target.id);
        }
      });
    }, { threshold: 0.6 });

    sections.forEach((section) => observer.observe(section));
    
    return () => {
      if (observer) {
        sections.forEach((section) => observer.unobserve(section));
      }
    };
  }, []);

  return (
    <header
      className={classNames(
        "fixed top-0 left-0 w-full px-6 transition-all duration-300 z-[90]",
        isScrolled ? "bg-[#00AA71] bg-opacity-80 backdrop-blur-md py-4" : "bg-transparent py-8"
      )}
    >
      <nav className="container mx-auto flex justify-between items-center">
        {/* Logo */}
        <div
          className={classNames(
            "font-bold transition-all duration-300",
            isScrolled ? "text-[#F9FD03] text-4xl" : "text-[#00AA71] text-6xl"
          )}
        >
          h3RnD3v
        </div>

        {/* Hamburger Button for Mobile */}
        <button
          className={classNames(
            "lg:hidden text-3xl focus:outline-none",
            !isScrolled ? "hidden" : ""
          )}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} />
        </button>

        {/* Navigation Links */}
        <ul
          className={classNames(
            "lg:flex lg:items-center lg:space-x-12 transition-all duration-300",
            isScrolled ? "text-lg" : "text-3xl",
            isMobileMenuOpen ? "flex" : "hidden",
            "lg:flex flex-col lg:flex-row absolute lg:relative top-full lg:top-auto left-0 w-full lg:w-auto bg-[#00AA71] lg:bg-transparent p-6 lg:p-0 space-y-6 lg:space-y-0 lg:space-x-12"
          )}
        >
          {navItems.map((item) => (
            <li key={item} className="relative group">
              <a
                href={`#${item}`}
                className={classNames(
                  "transition-all duration-300",
                  activeSection === item ? "text-[#F9FD03] font-bold scale-110" : "",
                  isScrolled || isDarkMode ? "text-white" : "text-gray-700"
                )}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.charAt(0).toUpperCase() + item.slice(1)}
              </a>
              {/* Underline Effect */}
              <span
                className={classNames(
                  "absolute left-0 bottom-0 h-[3px] transition-all duration-300",
                  activeSection === item ? "w-full bg-[#F9FD03]" : "w-0",
                  isDarkMode ? "bg-[#F9FD03]" : "",
                  isScrolled ? "bg-[#F9FD03]" : "bg-[#00AA71]"
                )}
              ></span>
            </li>
          ))}

          {/* Dark Mode Toggle */}
          <li>
            <button
              onClick={toggleTheme}
              className="relative w-16 h-8 bg-gray-300 dark:bg-gray-700 rounded-full p-1 flex items-center transition-all duration-300"
              aria-label={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
            >
              <div className={`w-7 h-7 bg-white dark:bg-yellow-400 rounded-full shadow-md flex items-center justify-center transform transition-transform duration-300 ${isDarkMode ? 'translate-x-7' : 'translate-x-0'}`}>
                <FontAwesomeIcon icon={isDarkMode ? faSun : faMoon} className="text-gray-800 dark:text-gray-100" />
              </div>
            </button>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default React.memo(Header);